import {
	Avatar,
	Button,
	Modal,
	Paper,
	Typography,
	useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import { InfinityPagination } from "../hooks/Pagination";
import { ManagerSearch, ManagerTable } from "../interfaces/Manager";
import { RequestTable } from "../interfaces/Request";
import { ResumeList } from "../interfaces/Resume";
import { Status } from "../interfaces/Status";
import { VacancySearch, VacancyTable } from "../interfaces/Vacancy";
import {
	GetManagers,
	GetResumes,
	GetStatuses,
	GetVacancies,
	UpdateRequest,
} from "../requester/functions";
import { MAIN_URL } from "../requester/requester";
import { getFullName } from "../utilities/string";
import CommentBox from "./CommentBox";
import CreateInterviewModal from "./CreateInterviewModal";
import CreateMailingModal from "./CreateMailingModal";
import DataMultiSelect from "./DataInputs/DataMultiSelect";
import EditMenu from "./EditMenu";

interface IProps {
	open: boolean;
	onClose(): void;
	onUpdate(id: number): void;
	request?: RequestTable;
	userId: number;
}

const UpdateRequestModal = (props: IProps) => {
	const theme = useTheme();

	const [request, setRequest] = useState<RequestTable>();
	const [status, setStatus] = useState<Status[]>([]);
	const [vacancy, setVacancy] = useState<VacancyTable[]>([]);
	const [managers, setManagers] = useState<ManagerTable[]>([]);
	const [watchers, setWatchers] = useState<ManagerTable[]>([]);

	const [resumes, setResumes] = useState<ResumeList[]>([]);

	const [searchManager, setSearchManager] = useState<ManagerSearch>({});
	const [searchWatcher, setSearchWatcher] = useState<ManagerSearch>({});
	const [searchVacancy, setSearchVacancy] = useState<VacancySearch>({});

	const [interviewRequest, setInterviewRequest] = useState<
		RequestTable | undefined
	>(undefined);

	const [mailingRequest, setMailingRequest] = useState<
		RequestTable | undefined
	>(undefined);

	const renderManager = (
		item: any,
		index: number,
		selectedId: number,
		rowId: number
	) => (
		<div
			key={`manager-${index}`}
			className="flex items-center w-full gap-3 rounded-lg px-1 py-1 cursor-pointer"
			style={
				selectedId === item.id
					? { border: "solid 2px", borderColor: theme.palette.primary.main }
					: {}
			}
			onClick={() => {
				setManager(item.id, rowId);
			}}
		>
			<Avatar src={`${MAIN_URL}manager/avatar/${item.avatar}`} />
			<Typography>
				{item.last_name} {item.first_name}
			</Typography>
		</div>
	);

	const renderVacancy = (
		item: any,
		index: number,
		selectedId: number,
		rowId: number
	) => (
		<div
			key={`vacancy-${index}`}
			className="flex items-center w-full gap-3 rounded-lg px-1 py-1 cursor-pointer"
			style={
				selectedId === item.id
					? { border: "solid 2px", borderColor: theme.palette.primary.main }
					: {}
			}
			onClick={() => {
				setRequestVacancy(item.id, rowId);
			}}
		>
			<Typography>{item.name}</Typography>
		</div>
	);

	const renderStatus = (
		item: any,
		index: number,
		selectedId: number,
		rowId: number
	) => (
		<div
			key={`status-${index}`}
			className="flex items-center w-full gap-3 rounded-lg px-1 py-1 cursor-pointer"
			style={
				selectedId === item.id
					? { border: "solid 2px", borderColor: theme.palette.primary.main }
					: {}
			}
			onClick={() => {
				setRequestStatus(item.id, rowId);
			}}
		>
			<Typography>{item.name}</Typography>
		</div>
	);

	const renderResume = (
		item: any,
		index: number,
		selectedId: number,
		rowId: number
	) => {
		return (
			<div
				key={`resume-${index}`}
				className="flex items-center w-full gap-3 rounded-lg px-1 py-1 cursor-pointer"
				style={
					selectedId === item.id
						? { border: "solid 2px", borderColor: theme.palette.primary.main }
						: {}
				}
				onClick={() => {
					setRequestResume(item.id, rowId);
				}}
			>
				<Typography>Резюме {index + 1}</Typography>
			</div>
		);
	};

	const setManager = (id: number, request_id: number) => {
		UpdateRequest({ id_manager: id, id: [request_id] }, (resp) => {
			props.onUpdate(request_id);
		});
	};

	const setRequestWatchers = (ids: number[], request_id: number) => {
		UpdateRequest({ watchers: ids, id: [request_id] }, (resp) => {
			props.onUpdate(request_id);
		});
	};

	const setRequestVacancy = (id: number, request_id: number) => {
		UpdateRequest({ id_vacancy: id, id: [request_id] }, (resp) => {
			props.onUpdate(request_id);
		});
	};

	const setRequestStatus = (id: number, request_id: number) => {
		UpdateRequest({ id_status: id, id: [request_id] }, (resp) => {
			props.onUpdate(request_id);
		});
	};

	const setRequestResume = (id: number, request_id: number) => {
		UpdateRequest({ id_resume: id, id: [request_id] }, (resp) => {
			props.onUpdate(request_id);
		});
	};

	useEffect(() => {
		if (props.request) {
			setRequest({ ...props.request });
		}
	}, [props.request]);

	useEffect(() => {
		GetManagers(
			{
				search_by: searchManager,
				pagination: InfinityPagination,
			},
			(resp) => {
				if (resp.status === "ok" && resp.data) {
					setManagers(resp.data.data);
				}
			}
		);
	}, [searchManager]);

	useEffect(() => {
		GetManagers(
			{
				search_by: searchWatcher,
				pagination: InfinityPagination,
			},
			(resp) => {
				if (resp.status === "ok" && resp.data) {
					setWatchers(resp.data.data);
				}
			}
		);
	}, [searchWatcher]);

	useEffect(() => {
		GetVacancies(
			{
				filter_by: { all_vacancies: true },
				pagination: InfinityPagination,
			},
			(resp) => {
				if (resp.status === "ok" && resp.data) {
					setVacancy(resp.data.data);
				}
			}
		);
	}, [searchManager]);

	useEffect(() => {
		GetStatuses({ filter_by: { type: "request" } }, (resp) => {
			if (resp.status === "ok" && resp.data) {
				setStatus(resp.data);
			}
		});

		GetResumes(
			{
				filter_by: { id_candidate: props.userId },
				pagination: InfinityPagination,
			},
			(resp) => {
				if (resp.status === "ok" && resp.data) {
					setResumes(resp.data.data);
				}
			}
		);
	}, []);

	return (
		<Modal
			open={props.open}
			onClose={props.onClose}
			className="flex items-center  flex-col justify-center  overflow-auto"
		>
			<Paper className="focus-visible:outline-none p-5 max-w-[800px] min-w-[400px] flex justify-between gap-3">
				<div className="flex flex-col">
					<Typography>{request?.candidate.full_name}</Typography>
					<Typography variant="caption" className="!mt-3">
						Статус
					</Typography>
					<EditMenu
						renderElement={renderStatus}
						data={status}
						selectedId={request?.status.id}
						item={request}
						hideSearch
					>
						{request?.status.name ? request?.status.name : "Нет"}
					</EditMenu>

					<Typography variant="caption" className="!mt-3">
						Вакансия
					</Typography>
					<EditMenu
						renderElement={renderVacancy}
						data={vacancy}
						search={searchVacancy.name}
						selectedId={request?.vacancy.id}
						item={request}
						setSearch={function (value: string) {
							setSearchVacancy((s) => {
								s.name = value;
								return { ...s };
							});
						}}
					>
						{request?.vacancy.name ? request?.vacancy.name : "Нет"}
					</EditMenu>

					<Typography variant="caption" className="!mt-3">
						Резюме
					</Typography>
					<EditMenu
						renderElement={renderResume}
						data={resumes}
						selectedId={request?.id_resume}
						item={request}
						hideSearch
					>
						{request?.id_resume ? "Резюме" : "Нет"}
					</EditMenu>

					<Typography variant="caption" className="!mt-3">
						Отвественный
					</Typography>
					<EditMenu
						renderElement={renderManager}
						data={managers}
						search={searchManager.full_name}
						selectedId={request?.manager.id}
						item={request}
						setSearch={function (value: string) {
							setSearchManager((s) => {
								s.full_name = value;
								return { ...s };
							});
						}}
					>
						{request?.manager.full_name ? request?.manager.full_name : "Нет"}
					</EditMenu>

					<DataMultiSelect
						label="Наблюдатели"
						list={watchers}
						objects
						value={request}
						setValue={(item: any) =>
							setRequestWatchers(item.watchers, request?.id ?? 0)
						}
						renderItemTitle={(item: ManagerTable) =>
							getFullName(item.last_name, item.first_name, item.patronymic)
						}
						keyValue={"watchers"}
					/>

					<div className="flex flex-col mt-auto">
						<Button onClick={() => setInterviewRequest(request)}>
							Назначить интервью
						</Button>
						<Button onClick={() => setMailingRequest(request)}>
							Отправить письмо
						</Button>
					</div>
				</div>
				<div className="-m-5 ml-0">
					<CommentBox type="request" id_obj={props.request?.id ?? 0} />
				</div>

				<CreateInterviewModal
					open={interviewRequest ? true : false}
					onClose={() => {
						setInterviewRequest(undefined);
					}}
					request={interviewRequest}
				/>

				<CreateMailingModal
					open={mailingRequest ? true : false}
					onClose={() => {
						setMailingRequest(undefined);
					}}
					request={mailingRequest}
				/>
			</Paper>
		</Modal>
	);
};

export default UpdateRequestModal;
